import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../shared.module';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { inOutAnimation } from '../../../animations';
import { enumToOptions, SelectOption } from '../../../inputs/select/model/select-option';
import { TariffPrivacyStatus } from '../../../models/price/tariff-privacy-status';
import { ToastService } from '../../../../services/toast.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LoaderService } from '../../../../services/loader.service';
import { PriceService } from '../../../../services/price.service';
import * as dayjs from 'dayjs';
import { finalize, Observable } from 'rxjs';
import { PriceDatePickerComponent } from './price-date-picker/price-date-picker.component';
import {
  currencyFromCountry,
  Price,
  PriceCountry,
  PriceDirection,
  PriceType,
  TimeSlot,
} from '../../../models/price/price';
import { AuthService } from '../../../../services/auth/auth.service';
import { UserRole } from '../../../models/user/user-role.enum';
import { UnsubscribeOnDestroyAdapter } from '../../../adapters/unsubscibe-on-destroy.adapter';
import { PricingListComponent } from './pricing-list/pricing-list/pricing-list.component';
import { UpsertBlockTariffComponent } from './pricing-list/upsert-block-tariff/upsert-block-tariff.component';

export const upsertPriceImports = [CommonModule, SharedModule, ReactiveFormsModule, TranslocoModule, PriceDatePickerComponent, RouterLink, PricingListComponent, UpsertBlockTariffComponent];
@Component({
  selector: 'psc-upsert-price',
  standalone: true,
  imports: upsertPriceImports,
  templateUrl: './upsert-price.component.html',
  animations: [
    inOutAnimation({}),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class UpsertPriceComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  formGroup: FormGroup;

  countriesOptions: SelectOption[] = enumToOptions(PriceCountry);
  typeOptions: SelectOption[] = enumToOptions(PriceType);
  privacyStatusOptions: SelectOption[] = enumToOptions(TariffPrivacyStatus, [TariffPrivacyStatus.PRIVATE]);
  userIsAdmin: boolean;
  blockTariff: boolean;
  upsertPriceErrors: UpsertPriceErrors = {
    datesAreNotAYear: false,
    timeslotsAreNotDayError: false,
  };

  protected readonly UserRole: typeof UserRole = UserRole;

  abstract get backRoute(): string;

  get pricingList(): FormArray {
    return this.formGroup.controls['pricingList'] as FormArray;
  }

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected router: Router,
    protected loaderService: LoaderService,
    protected priceService: PriceService,
    protected route: ActivatedRoute,
    private authService: AuthService,
    private translocoService: TranslocoService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.userIsAdmin = this.authService.userHasRole(UserRole.ROLE_ADMIN);
    this.blockTariff = this.route.snapshot.queryParams['type'] === 'block';
    if (this.blockTariff) {
      this.typeOptions = enumToOptions(PriceType, [PriceType.ENTERPRISE]);
    } else {
      this.typeOptions = enumToOptions(PriceType);
    }

    this.formGroup = this.formBuilder.group({
      priceId: '',
      name: [this.userIsAdmin ? '' : this.translocoService.translate('prices.my-price'), Validators.required],
      tariffModel: ['', Validators.required],
      country: ['', Validators.required],
      currency: [''],
      type: ['', Validators.required],
      tax: ['', [Validators.required, Validators.min(0), Validators.max(100)]],
      direction: [PriceDirection.BUY, Validators.required],
      privacyStatus: [this.userIsAdmin ? '' : TariffPrivacyStatus.PRIVATE, Validators.required],
      saturday: [false, Validators.required],
      sunday: [false, Validators.required],
      pricingList: this.formBuilder.array([]),
      blockList: this.formBuilder.array([]),
    });
  }



  parseFormValueProperly(price: any) {
    if (!(price.pricingList?.length > 0)) {
      return;
    }
    for (const pricingListElement of price.pricingList) {
      pricingListElement.dateFrom = this.dateToDTODate(pricingListElement.dateFrom);
      pricingListElement.dateTo = this.dateToDTODate(pricingListElement.dateTo);
    }
  }

  timeToDate(time: string) {
    return dayjs(`2021-01-01 ${ time }`);
  }

  dateToDTODate(dateFormValue: { month: number, date: number }) {
    return `--${ dateFormValue.month }-${ dateFormValue.date }`;
  }

  savePrice() {
    this.loaderService.showLoader();
    let valid = true;
    if (!this.validateTimeslotsAreDay()) {
      valid = false;
      /**
       * t(error.check_form)
       */
      this.toastService.showErrorToast('error.check_form');
      this.upsertPriceErrors.timeslotsAreNotDayError = true;
    }
    if (!this.validateDatesAreYear()) {
      valid = false;
      this.upsertPriceErrors.datesAreNotAYear = true;
    }
    if (!valid) {
      this.loaderService.hideLoader();
      return;
    }
    this.formGroup.controls['currency'].setValue(currencyFromCountry(this.formGroup.controls['country'].value));
    this.saveToApi().pipe(
      finalize(() => {
        this.loaderService.hideLoader();
      }),
    ).subscribe(() => {
      this.router.navigate([this.backRoute], { relativeTo: this.route });
      this.toastService.showSuccessToast();
    });
  }


  protected abstract saveToApi(): Observable<Price>;

  private validateDatesAreYear() {
    if (!(this.pricingList?.length > 0)) {
      return true;
    }
    const daysArray: number[] = [];
    const yearStart = dayjs('2021-01-01');
    const yearEnd = dayjs('2021-12-31');
    for (const pricingListElement of this.pricingList.value) {
      const dateFrom = dayjs(`2021-${ pricingListElement.dateFrom.month }-${ pricingListElement.dateFrom.date } `);
      const dateTo = dayjs(`2021-${ pricingListElement.dateTo.month }-${ pricingListElement.dateTo.date } `);
      if (dateFrom < dateTo) {
        daysArray.push(dateTo.diff(dateFrom, 'd'));
      } else if (dateFrom > dateTo) {
        daysArray.push(yearEnd.diff(dateFrom, 'd'));
        daysArray.push(dateTo.diff(yearStart, 'd'));
      }
    }
    const sum = daysArray.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return (sum + daysArray.length) === 365;
  }

  private validateTimeslotsAreDay() {
    if (!(this.pricingList?.length > 0)) {
      return true;
    }
    for (const pricingListElement of this.pricingList.value) {
      if (!this.validateTimeSlots(pricingListElement.timeSlots)) {
        return false;
      }
    }
    return true;
  }

  private validateTimeSlots(timeSlots: TimeSlot[]) {
    const hoursArray: number[] = [];
    const dayStart = this.timeToDate('00:00:00');
    const dayEnd = this.timeToDate('23:59:59');
    for (const timeSlot of timeSlots) {
      const timeFrom = this.timeToDate(timeSlot.timeFrom);
      const timeTo = this.timeToDate(timeSlot.timeTo);
      if (timeFrom < timeTo) {
        hoursArray.push(timeTo.diff(timeFrom, 's'));
      } else if (timeFrom > timeTo) {
        hoursArray.push(dayEnd.diff(timeFrom, 's'));
        hoursArray.push(timeTo.diff(dayStart, 's'));
      }
    }
    const sum = hoursArray.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return sum === 86399 || sum === 86400; // Full day - 1 second
  }
}

export interface UpsertPriceErrors {
  datesAreNotAYear: boolean;
  timeslotsAreNotDayError: boolean;
}
